import { useEffect } from 'react';
import { Redirect } from 'react-router-dom';
import { useQuery } from 'react-query';

import useSessionStore from 'src/stores/session';
import { ROUTES_URL } from 'src/constants/routes';
import useUserStore from 'src/stores/user';
import { BEGIN_WORK_STEPS_IDS } from 'src/constants/beginWorksSteps';
import AppRouteLoader from 'src/components/Loading/AppRouteLoader';
import { clearAuthDestination, pendingEntryDestination } from 'src/utils/authContinuation';

function ContinueAfterLogin({ to }: { to: string }) {
    useEffect(() => clearAuthDestination(), []);
    return <Redirect to={to} />;
}

export default function MainRoute() {
    const isAuthorized = useSessionStore(({ isAuthorized }) => isAuthorized);
    const { isEmailConfirmed, isOnboardingCompleted, onboardingStepName } = useUserStore(
        ({ isEmailConfirmed, isOnboardingCompleted, onboardingStepName }) => ({
            isEmailConfirmed,
            isOnboardingCompleted,
            onboardingStepName,
        })
    );
    const { getUserSessionRequiredData, hasSessionSnapshot } = useSessionStore(
        ({ getUserSessionRequiredData, hasSessionSnapshot }) => ({
            getUserSessionRequiredData,
            hasSessionSnapshot,
        })
    );

    // Используем кеш из react-query - если данные уже загружены, запрос не выполнится
    const { isLoading } = useQuery('user', getUserSessionRequiredData, {
        enabled: isAuthorized, // Загружаем только если авторизован
        staleTime: 30000, // Данные считаются свежими 30 секунд
        cacheTime: 60000, // Кеш хранится 60 секунд
    });

    if (!isAuthorized) return <Redirect to={ROUTES_URL.LOGIN} />;

    // Не показываем редирект пока загружаются данные (только при первой загрузке)
    if (isLoading || !hasSessionSnapshot) return <AppRouteLoader />;

    if (!isEmailConfirmed) return <Redirect to={ROUTES_URL.WELCOME_SCREEN} />;
    const continuation = pendingEntryDestination();
    if (continuation) {
        return <ContinueAfterLogin to={continuation} />;
    }
    // Both variants stay opt-in: remote capabilities must not change the existing entry flow.

    if (!isOnboardingCompleted) {
        if (onboardingStepName === BEGIN_WORK_STEPS_IDS.DESCRIPTION) {
            return <Redirect to={ROUTES_URL.BEGIN_WORK_DESCRIPTION} />;
        }

        if (onboardingStepName === BEGIN_WORK_STEPS_IDS.CHOOSE_STREAM) {
            return <Redirect to={ROUTES_URL.LEADSTREAM} />;
        }

        return <Redirect to={ROUTES_URL.BEGIN_WORK} />;
    }

    return <Redirect to={ROUTES_URL.FEED} />;
}
